import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useSiteData } from '../contexts/SiteDataContext';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const { artistInfo, navLinks } = useSiteData();

  const handleScroll = useCallback(() => {
    setIsScrolled(window.scrollY > 40);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  const name = artistInfo?.name || 'Sebastian McLaughlin';

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-[0_1px_0_rgba(0,0,0,0.04)]'
          : 'bg-white'
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-[72px]">
          <Link
            to="/"
            data-testid="header-logo-link"
            className="text-[15px] font-medium tracking-[0.02em] text-[#1a1a1a] hover:text-[#555] transition-colors duration-300"
          >
            {name}
          </Link>

          <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.name.toLowerCase()}`}
                className={`text-[13px] tracking-[0.01em] transition-all duration-300 ${
                  location.pathname === link.path
                    ? 'text-[#1a1a1a] font-medium'
                    : 'text-[#888] hover:text-[#1a1a1a]'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          <button
            onClick={() => setIsMenuOpen((prev) => !prev)}
            data-testid="mobile-menu-toggle"
            className="md:hidden text-[#555] hover:text-[#1a1a1a] transition-colors p-2"
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      <div
        className={`md:hidden overflow-hidden transition-all duration-500 ease-in-out ${
          isMenuOpen ? 'max-h-[400px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <nav className="bg-white border-t border-[#f0f0f0] px-6 py-8" data-testid="mobile-nav">
          <div className="flex flex-col gap-5">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
                className={`text-[14px] transition-colors duration-300 ${
                  location.pathname === link.path
                    ? 'text-[#1a1a1a] font-medium'
                    : 'text-[#888] hover:text-[#1a1a1a]'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Header;
